<template>
  <teleport to="body">
    <div v-if="show" @click="handleBackdropClick" class="backdrop"></div>
    <transition name="dialog" >
      <dialog open v-if="show" :id="dialogId">
        <header>
          <slot name="header">
            <h2>{{ title }}</h2>
          </slot>
        </header>
        <section>
          <slot></slot>
        </section>
        <menu v-if="!fixed">
          <slot name="actions">
            <base-button @click="tryClose">Close</base-button>
          </slot>
        </menu>
      </dialog>
    </transition>
  </teleport>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

/**
 * BaseDialog - Modal dialog component
 * 
 * Displays content in a modal overlay with optional header, body, and actions.
 * Supports teleporting to body, backdrop click to close, and fixed mode.
 * 
 * @example
 * <base-dialog :show="isOpen" @close="isOpen = false" title="Confirmation">
 *   <p>Are you sure?</p>
 *   <template #actions>
 *     <base-button @click="confirm">Yes</base-button>
 *     <base-button mode="flat" @click="isOpen = false">No</base-button>
 *   </template>
 * </base-dialog>
 */
export default defineComponent({
  name: 'BaseDialog',
  props: {
    /**
     * Controls dialog visibility
     */
    show: {
      type: Boolean,
      required: true,
    },
    /**
     * Dialog title displayed in header
     */
    title: {
      type: String,
      required: false,
      default: ''
    },
    /**
     * Unique dialog ID for accessibility
     */
    dialogId: {
      type: String,
      required: false,
      default: ''
    },
    /**
     * If true, prevents closing via close button or backdrop
     */
    fixed: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * If true, clicking backdrop closes the dialog
     */
    closeOnClickOutside: {
      type: Boolean,
      default: true,
    },
  },
  emits: {
    /**
     * Emitted when user tries to close the dialog
     */
    close: () => true
  },
  setup(props, { emit }) {
    const tryClose = () => {
      if (props.fixed) {
        return;
      }
      emit('close');
    };

    const handleBackdropClick = () => {
      if (props.closeOnClickOutside) {
        emit('close');
      }
    };

    return {
      tryClose,
      handleBackdropClick
    };
  }
});
</script>

<style scoped>
.backdrop {
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  width: 100%;
  background-color: var(--color-overlay-dark);
  z-index: var(--z-index-modal-backdrop);
  backdrop-filter: var(--backdrop-blur-sm);
}

dialog {
  position: fixed;
  top: 20vh;
  left: 10%;
  width: 80%;
  z-index: var(--z-index-modal);
  border-radius: var(--radius-xl);
  border: none;
  box-shadow: var(--shadow-2xl);
  padding: 0;
  margin: 0;
  overflow: hidden;
  background-color: var(--color-bg-primary);
}

header {
  background: var(--gradient-primary);
  color: var(--color-text-inverse);
  width: 100%;
  padding: var(--spacing-4);
}

header h2 {
  margin: 0;
  font-weight: var(--font-weight-bold);
}

section {
  padding: var(--spacing-6);
}

menu {
  padding: var(--spacing-6);
  display: flex;
  justify-content: flex-end;
  gap: var(--spacing-2);
  margin: 0;
  border-top: var(--border-light);
  background: var(--color-bg-secondary);
}

.dialog-enter-from,
.dialog-leave-to {
  opacity: 0;
  transform: scale(0.8);
}

.dialog-enter-active {
  transition: var(--transition-base);
}

.dialog-leave-active {
  transition: var(--transition-base);
}

.dialog-enter-to,
.dialog-leave-from {
  opacity: 1;
  transform: scale(1);
}

@media (min-width: 768px) {
  dialog {
    left: calc(50% - 20rem);
    width: 40rem;
  }
}
</style>